import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, Download, Paperclip, Share2 } from "lucide-react";
import type { ViewerCover } from "@demore/shared/types";
import { isHttpUrl } from "@demore/shared/validation";
import { ViewerShell } from "./ViewerShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { ViewerSurveys } from "./ViewerSurveys";

type ViewerAttachment = {
  id: string;
  filename: string;
  contentType: string | null;
  sizeBytes: number | null;
  videoTitle: string;
};

function prettySize(b: number | null) {
  if (b === null) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

export function DonePage() {
  const { token } = useParams<{ token: string }>();
  const { data } = useQuery({
    queryKey: ["viewer-cover", token],
    queryFn: () => api.get<ViewerCover>(`/api/view/${token}`),
    enabled: !!token,
  });

  const [forwardName, setForwardName] = useState("");
  const [forwardEmail, setForwardEmail] = useState("");
  const forward = useMutation({
    mutationFn: () => api.post(`/api/view/${token}/forward`, { name: forwardName, email: forwardEmail }),
    onSuccess: () => { toast.success("Sent — they'll receive a personalized link."); setForwardName(""); setForwardEmail(""); },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Forward failed"),
  });

  const { data: attachments } = useQuery({
    queryKey: ["viewer-attachments", token],
    queryFn: () => api.get<{ items: ViewerAttachment[] }>(`/api/view/${token}/attachments`),
    enabled: !!token,
  });

  async function downloadAttachment(attId: string, filename: string) {
    try {
      const { downloadUrl } = await api.get<{ downloadUrl: string }>(`/api/view/${token}/attachments/${attId}/download`);
      // Open in a new tab; Azure Blob serves with content-disposition.
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = filename;
      a.target = "_blank";
      a.rel = "noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    }
  }

  return (
    <ViewerShell>
      <div className="space-y-8 py-6">
        <section className="text-center space-y-2">
          <h1 className="font-head text-3xl md:text-4xl font-semibold">Thank you, {data?.recipientName ?? "there"}.</h1>
          <p className="text-navy-200">
            That's the end of the pack. {data?.senderFirstName ?? "Your contact"} will be in touch.
          </p>
        </section>

        {data?.ctaLabel && data.ctaUrl && isHttpUrl(data.ctaUrl) && (
          <div className="text-center">
            <a href={data.ctaUrl} target="_blank" rel="noreferrer">
              <Button size="lg">{data.ctaLabel} <ArrowRight className="h-4 w-4 ml-1" /></Button>
            </a>
          </div>
        )}

        {/* CEO Rev 1.0 Phase 2: campaign surveys (e.g. Post-Onboarding Review). */}
        <ViewerSurveys token={token} />

        {attachments && attachments.items.length > 0 && (
          <section className="bg-viewer-soft rounded-md p-5 space-y-3">
            <div className="flex items-center gap-2 font-head text-lg">
              <Paperclip className="h-4 w-4" /> Resources to keep
            </div>
            <ul className="space-y-2">
              {attachments.items.map((a) => (
                <li key={a.id} className="flex items-center justify-between gap-3 bg-navy-500 rounded-md p-3">
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{a.filename}</div>
                    <div className="text-xs text-navy-200">
                      from "{a.videoTitle}"{a.sizeBytes ? ` · ${prettySize(a.sizeBytes)}` : ""}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => downloadAttachment(a.id, a.filename)}>
                    <Download className="h-4 w-4" /> Download
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {data?.allowForwarding && (
          <section className="bg-viewer-soft rounded-md p-5 space-y-3">
            <div className="flex items-center gap-2 font-head text-lg">
              <Share2 className="h-4 w-4" /> Share with a colleague
            </div>
            <p className="text-sm text-navy-200">They'll get their own personalized link.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Their name</Label><Input value={forwardName} onChange={(e) => setForwardName(e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Their email</Label><Input type="email" value={forwardEmail} onChange={(e) => setForwardEmail(e.target.value)} /></div>
            </div>
            <Button disabled={!forwardName || !forwardEmail || forward.isPending} onClick={() => forward.mutate()}>
              {forward.isPending ? "Sending…" : "Send forward"}
            </Button>
          </section>
        )}
      </div>
    </ViewerShell>
  );
}
